import type { FeatureKey } from "./definitions";

// One row per active (user, company) pair, mirroring the SignupFunnel spine.
// Drives all client-side dimension filtering (cohort month, country, role,
// company size, support exclusion).
export const userDimsSQL = `
WITH company_size AS (
  SELECT
    ur.company_external_id,
    COUNT(DISTINCT ur.user_external_id) AS marloo_active_user_count
  FROM public.user_roles ur
  WHERE ur.status = 'active'
  GROUP BY ur.company_external_id
)
SELECT
  ur.user_external_id,
  ur.company_external_id,
  ur.role::text          AS user_role,
  cmp.country_code,
  COALESCE(cs.marloo_active_user_count, 0) AS marloo_active_user_count,
  u.created_at_utc       AS signup_date_utc
FROM public.user_roles ur
JOIN public.users      u   ON u.external_id = ur.user_external_id
LEFT JOIN public.companies cmp ON cmp.external_id = ur.company_external_id
LEFT JOIN company_size      cs ON cs.company_external_id = ur.company_external_id
WHERE ur.status = 'active'
  AND u.email NOT ILIKE '%@gomarloo.com'
`;

// Long-format daily activity over the last ~14 weeks. One row per
// (user, company, day, feature) pair. Aggregated server-side to keep payload
// modest (~40k rows for the active dataset).
//
// Tasks attribution: chat_message rows count as `tasks` when the parent chat
// has type='task' OR the message has a chat_message_task_part row attached.
// Otherwise chat_message rows under chat.type='client' are bucketed as
// `client`. Pure 'general' chat is excluded for v1.
//
// chat.type was only populated starting ~2026-04-20. For earlier rows we
// derive an effective type from the FK columns on chat (client_external_id
// implies a client-view chat, etc.) so the pre-rollout history isn't lost.
export const activitySQL = `
WITH window_start AS (
  SELECT (date_trunc('week', now()) - interval '14 weeks')::timestamptz AS ts
),
ev AS (
  SELECT
    ms.user_external_id,
    ms.company_external_id,
    ms.created_date_utc::timestamptz AS ts,
    'meetings'::text AS feature
  FROM public.meeting_synopsis ms
  CROSS JOIN window_start ws
  WHERE ms.created_date_utc >= ws.ts
    AND ms.user_external_id IS NOT NULL

  UNION ALL

  SELECT
    d.user_external_id,
    d.company_external_id,
    d.created_date_utc::timestamptz,
    'documents'
  FROM public.document d
  CROSS JOIN window_start ws
  WHERE d.created_date_utc >= ws.ts
    AND d.user_external_id IS NOT NULL

  UNION ALL

  SELECT
    ff.created_by_user_external_id,
    ff.company_external_id,
    ff.created_date_utc::timestamptz,
    'forms'
  FROM public.filled_form ff
  CROSS JOIN window_start ws
  WHERE ff.created_date_utc >= ws.ts
    AND ff.created_by_user_external_id IS NOT NULL

  UNION ALL

  SELECT
    ta.created_by_user_external_id,
    ta.company_external_id,
    ta.created_date_utc::timestamptz,
    'tasks'
  FROM public.task_activity ta
  CROSS JOIN window_start ws
  WHERE ta.created_date_utc >= ws.ts
    AND ta.activity_type = 'task-updated'
    AND ta.created_by_user_external_id IS NOT NULL

  UNION ALL

  SELECT
    c.user_external_id,
    cm.company_external_id,
    cm.created_date_utc::timestamptz,
    CASE
      WHEN c.type::text = 'task'
        OR EXISTS (
          SELECT 1 FROM public.chat_message_task_part p
          WHERE p.message_external_id = cm.external_id
        )
      THEN 'tasks'
      ELSE 'client'
    END
  FROM public.chat_message cm
  JOIN public.chat c ON c.external_id = cm.chat_external_id
  CROSS JOIN window_start ws
  WHERE cm.created_date_utc >= ws.ts
    AND cm.role::text = 'user'
    AND c.user_external_id IS NOT NULL
    AND (
      c.type::text IN ('client','task')
      OR (c.type IS NULL AND c.client_external_id IS NOT NULL)
      OR EXISTS (
        SELECT 1 FROM public.chat_message_task_part p
        WHERE p.message_external_id = cm.external_id
      )
    )
)
SELECT
  user_external_id,
  company_external_id,
  ts::date                                  AS day,
  date_trunc('week', ts)::date              AS week_start,
  feature
FROM ev
GROUP BY 1, 2, 3, 4, 5
`;

export type UserDimRow = {
  user_external_id: string;
  company_external_id: string | null;
  user_role: string | null;
  country_code: string | null;
  marloo_active_user_count: number;
  signup_date_utc: string;
  // Stubs to satisfy FilterableUser. WAU page does not propagate demos.
  had_demo: boolean;
  first_demo_date: string | null;
  // Required by FilterableUser shape (cohort month derives from signup).
  created_at_utc: string;
};

export type ActivityRow = {
  user_external_id: string;
  company_external_id: string | null;
  day: string;
  week_start: string;
  feature: FeatureKey;
};

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string => (v == null ? "" : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : String(v));

function indexer(columns: string[]): Record<string, number> {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return idx;
}

export function mapUserDimRows(columns: string[], rows: unknown[][]): UserDimRow[] {
  const idx = indexer(columns);
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  return rows.map((row) => {
    const signup = str(get(row, "signup_date_utc"));
    return {
      user_external_id: str(get(row, "user_external_id")),
      company_external_id: strOrNull(get(row, "company_external_id")),
      user_role: strOrNull(get(row, "user_role")),
      country_code: strOrNull(get(row, "country_code")),
      marloo_active_user_count: num(get(row, "marloo_active_user_count")),
      signup_date_utc: signup,
      had_demo: false,
      first_demo_date: null,
      created_at_utc: signup,
    };
  });
}

const FEATURE_SET = new Set<FeatureKey>(["meetings", "client", "documents", "tasks", "forms"]);

export function mapActivityRows(columns: string[], rows: unknown[][]): ActivityRow[] {
  const idx = indexer(columns);
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  const out: ActivityRow[] = [];
  for (const row of rows) {
    const feature = str(get(row, "feature"));
    if (!FEATURE_SET.has(feature as FeatureKey)) continue;
    out.push({
      user_external_id: str(get(row, "user_external_id")),
      company_external_id: strOrNull(get(row, "company_external_id")),
      day: str(get(row, "day")).slice(0, 10),
      week_start: str(get(row, "week_start")).slice(0, 10),
      feature: feature as FeatureKey,
    });
  }
  return out;
}
